import React from 'react'
import { useIntl } from 'react-intl'
import { KTIcon } from '../../../../_metronic/helpers'
import { SmartTooltip } from '../../../../components/SmartTooltip/SmartTooltip'
import { formatSum } from '../../../../utils'
import { OutcomeBreakdownRow } from '../core/_models'

type Props = {
  className: string
  color: string
  status: string
  count: number
  commission?: string | null
  sum: string
  /** Total tickets count across all statuses of the current selection — enables the share label */
  total?: number
  /** The same count split by why the tickets ended that way. Deposits: every final status.
   *  Payouts: cancelled only — the paid card carries no breakdown at all. */
  breakdown?: Array<OutcomeBreakdownRow>
}

const formatPercent = (count: number, total: number): string => {
  const percent = total > 0 ? (count / total) * 100 : 0

  return `${percent.toFixed(1).replace('.', ',')}%`
}

const ReportBlock: React.FC<Props> = ({
  className,
  color,
  status,
  count,
  commission,
  sum,
  total,
  breakdown,
}) => {
  const intl = useIntl()

  // Незакрытые статусы приходят одной строкой без причины — разбор там пустой по смыслу,
  // и подсказка «причина не зафиксирована: 100%» только сбивала бы с толку.
  const rows = breakdown?.filter((row) => row.code !== null) ?? []
  const hasBreakdown = rows.length > 0

  const cell: React.CSSProperties = { paddingTop: 4, paddingBottom: 2, color: 'var(--bs-body-color)' }
  const numericCell: React.CSSProperties = { ...cell, textAlign: 'right', whiteSpace: 'nowrap' }
  const headCell: React.CSSProperties = { paddingBottom: 4, fontWeight: 600, color: 'var(--bs-body-color)' }

  const popover = (
    <div
      style={{
        backgroundColor: 'var(--bs-body-bg)',
        color: 'var(--bs-body-color)',
        border: '1px solid var(--bs-border-color)',
        borderRadius: 6,
        boxShadow: '0 2px 8px rgba(0,0,0,.15)',
        padding: '8px 12px',
        minWidth: 420,
      }}
    >
      <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 12 }}>
        <thead>
          <tr style={{ borderBottom: '1px solid var(--bs-border-color)' }}>
            <th style={{ ...headCell, textAlign: 'left' }}>Причина</th>
            <th style={{ ...headCell, textAlign: 'right' }}>Кол-во</th>
            <th style={{ ...headCell, textAlign: 'right' }}>Сумма</th>
            <th style={{ ...headCell, textAlign: 'right' }}>Доля</th>
          </tr>
        </thead>
        <tbody>
          {(breakdown ?? []).map((row) => (
            <tr key={row.code ?? 'unknown'} style={{ borderBottom: '1px solid var(--bs-border-color)' }}>
              <td style={cell}>{row.title ?? 'Причина не зафиксирована'}</td>
              <td style={numericCell}>{row.ticketsCount}</td>
              <td style={numericCell}>{formatSum(row.sum)}</td>
              <td style={{ ...numericCell, color: 'var(--bs-secondary-color)' }}>
                {total === undefined ? '—' : formatPercent(row.ticketsCount, total)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <p style={{ margin: '6px 0 0', fontSize: 11, color: 'var(--bs-secondary-color)' }}>
        Доля считается от всех заявок выборки, а не от этой карточки.
      </p>
    </div>
  )

  return (
    <div className={`card ${className}`}>
      <div className='card-body p-0'>
        <div className={`px-9 pt-7 card-rounded h-300px w-100 bg-${color}`}>
          <h3 className='m-0 text-white fw-bold fs-1 text-center'>
            {intl.formatMessage({ id: 'ticket.status.' + status })}
          </h3>
          <div className='d-flex text-center flex-column text-white pt-8'>
            <span className='fw-semibold fs-3'>Количество: </span>
            <span className='fw-semibold fs-1'>
              {count}
              {total === undefined ? null : ` (${formatPercent(count, total)})`}
            </span>
          </div>
          <div
            className={`d-flex text-center text-white pt-8 ${
              commission ? 'justify-content-between' : 'justify-center'
            }`}
          >
            <div className='d-flex flex-column'>
              <span className='fw-semibold fs-3'>Сумма: </span>
              <span className='fw-bold fs-2x pt-1'>{sum}</span>
            </div>
            {commission ? (
              <div className='d-flex flex-column'>
                <span className='fw-semibold fs-3'>Комиссия: </span>
                <span className='fw-bold fs-2x pt-1'>{commission}</span>
              </div>
            ) : null}
          </div>
          {hasBreakdown ? (
            <div className='d-flex justify-content-center pt-4'>
              <SmartTooltip content={popover} bare maxWidth={560} maxHeight={420}>
                <span
                  className='text-white d-inline-flex align-items-center'
                  style={{ cursor: 'help', opacity: 0.85, fontSize: 13 }}
                  tabIndex={0}
                >
                  <KTIcon iconName='information-2' className='fs-5 me-1' />
                  Подробнее
                </span>
              </SmartTooltip>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  )
}

export { ReportBlock }
